import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqAccordionProps {
  items: FaqItem[];
  title?: string;
  /** Pass page URL so search engines can map the FAQPage entity to the page. */
  pageUrl?: string;
}

/** Pure JSON-LD builder — exported so tests/snapshots can validate it. */
export function buildFaqLd(items: FaqItem[], pageUrl?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageUrl ? { url: pageUrl } : {}),
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } as const;
}

/**
 * Renders FAQs as an accessible accordion AND emits valid FAQPage JSON-LD
 * built from the same source array. Single source of truth.
 */
export function FaqAccordion({ items, title, pageUrl }: FaqAccordionProps) {
  if (!items.length) return null;

  const ld = buildFaqLd(items, pageUrl);

  return (
    <section className="mt-12">
      {title && (
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      )}
      <Accordion type="single" collapsible className="mt-4">
        {items.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`}>
            <AccordionTrigger className="text-left text-base">{f.q}</AccordionTrigger>
            <AccordionContent className="text-base text-foreground/90">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
      />
    </section>
  );
}